// src/pages/shop/ShopInventory.jsx
//
// Инвентаризация — сессия сверки остатков с фактом.
// Сканируешь код → видишь «по программе X» → вводишь факт → строка с расхождением.
// Пока не завершил — остатки не трогаются. «Завершить» подгоняет остатки под факт.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { products as productsApi, inventory as inventoryApi } from '../../api';
import { useMyCurrency } from '../../hooks/useMyShop';
import { useToast } from '../../hooks/useToast';
import { formatMoney } from '../../data/currencies';
import { errorMessage } from '../../utils/errorMessage';
import ScanButton from '../../components/ScanButton';
import Icon from '../../components/Icon';

function variantLabel(product, variant) {
  const av = (variant && variant.attributeValues && typeof variant.attributeValues === 'object')
    ? Object.values(variant.attributeValues).filter(Boolean).join(' / ')
    : '';
  const vn = (variant && variant.name && variant.name.trim()) ? variant.name.trim() : av;
  return vn ? `${product.name} · ${vn}` : product.name;
}

export default function ShopInventory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currency = useMyCurrency();
  const { show, ToastView } = useToast();

  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState(null);   // { code, name, expected, unit }
  const [actual, setActual] = useState('');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [tab, setTab] = useState('count');      // 'count' | 'history'
  const [history, setHistory] = useState(null);
  const [detail, setDetail] = useState(null);   // раскрытая сессия

  // Старт/получение открытой сессии
  useEffect(() => {
    (async () => {
      try {
        const s = await inventoryApi.start();
        setSession(s);
        setItems(s.items || []);
      } catch (e) {
        show(errorMessage(e, t), 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Скан → превью «по программе X»
  const onScan = async (code) => {
    if (!code) return;
    try {
      const r = await productsApi.byBarcode(code);
      if (!r || !r.product) {
        show(t('inventory.notFound', 'Товар не найден — занеси его сначала'), 'error');
        return;
      }
      const expected = Number(r.variant?.stock) || 0;
      setScan({
        code,
        name: variantLabel(r.product, r.variant),
        expected,
        unit: r.product.unit || 'шт',
      });
      setActual(String(expected));
    } catch (e) {
      show(t('inventory.notFound', 'Товар не найден — занеси его сначала'), 'error');
    }
  };

  // Сохранить факт по позиции
  const saveActual = async () => {
    if (!scan || !session) return;
    setBusy(true);
    try {
      const item = await inventoryApi.addItem(session.id, scan.code, Number(actual) || 0);
      setItems(prev => {
        const rest = prev.filter(i => i.variantId !== item.variantId);
        return [item, ...rest];
      });
      setScan(null);
      setActual('');
    } catch (e) {
      show(errorMessage(e, t), 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (id) => {
    try {
      await inventoryApi.removeItem(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
  };

  const finish = async () => {
    if (!session || items.length === 0) return;
    setClosing(true);
    try {
      await inventoryApi.close(session.id);
      show(t('inventory.done', 'Инвентаризация завершена, остатки обновлены'), 'success');
      navigate(-1);
    } catch (e) {
      show(errorMessage(e, t), 'error');
      setClosing(false);
    }
  };

  const cancel = async () => {
    if (!session) { navigate(-1); return; }
    try { await inventoryApi.cancel(session.id); } catch (_) {}
    navigate(-1);
  };

  // История завершённых сессий
  useEffect(() => {
    if (tab !== 'history' || history !== null) return;
    (async () => {
      try {
        const r = await inventoryApi.history();
        setHistory(r.sessions || []);
      } catch (e) {
        setHistory([]);
        show(errorMessage(e, t), 'error');
      }
    })();
  }, [tab]);

  const openDetail = async (id) => {
    if (detail && detail.id === id) { setDetail(null); return; }
    try {
      const s = await inventoryApi.detail(id);
      setDetail(s);
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
  };

  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString(); } catch (_) { return ''; }
  };

  // Сводка расхождений
  let shortage = 0, surplus = 0, shortageCost = 0;
  for (const it of items) {
    const d = (Number(it.actual) || 0) - (Number(it.expected) || 0);
    if (d < 0) { shortage += -d; shortageCost += (-d) * (Number(it.costPrice) || 0); }
    else if (d > 0) { surplus += d; }
  }

  return (
    <div className="page" style={{ paddingBottom: 100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>
          {t('inventory.title', 'Инвентаризация')}
        </h1>
      </div>

      {/* Вкладки */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: 'var(--bg-elevated)', padding: 4, borderRadius: 'var(--r-md)' }}>
        {['count', 'history'].map(tb => (
          <button key={tb} onClick={() => setTab(tb)} style={{
            flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer',
            borderRadius: 'var(--r-sm)', fontWeight: 700, fontSize: 13,
            background: tab === tb ? 'var(--brand-primary)' : 'transparent',
            color: tab === tb ? '#fff' : 'var(--fg-muted)',
          }}>
            {tb === 'count' ? t('inventory.tabCount', 'Пересчёт') : t('inventory.tabHistory', 'История')}
          </button>
        ))}
      </div>

      {tab === 'count' && (loading ? (
        <p style={{ color: 'var(--fg-muted)' }}>{t('common.loading', 'Загрузка…')}</p>
      ) : (
        <>
          {/* Скан */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: 16, borderRadius: 'var(--r-md)',
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            marginBottom: 16,
          }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 15, fontWeight: 700 }}>{t('inventory.scanTitle', 'Сканируй товар')}</p>
              <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>
                {t('inventory.scanHint', 'Штрихкод или Честный знак — найдём нужный вариант')}
              </p>
            </div>
            <ScanButton onScan={onScan} size="lg" />
          </div>

          {/* Сводка */}
          {items.length > 0 && (
            <div style={{
              display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap',
            }}>
              <Chip label={t('inventory.counted', 'Посчитано')} value={`${items.length}`} />
              <Chip label={t('inventory.shortage', 'Недостача')} value={`−${shortage}`} tone="danger" />
              <Chip label={t('inventory.surplus', 'Излишек')} value={`+${surplus}`} tone="success" />
              {shortageCost > 0 && (
                <Chip label={t('inventory.lossCost', 'Потери')} value={formatMoney(shortageCost, currency)} tone="danger" />
              )}
            </div>
          )}

          {/* Список */}
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--fg-faint)' }}>
              <Icon name="list" size={40} />
              <p style={{ marginTop: 12, fontSize: 14 }}>
                {t('inventory.empty', 'Пока ничего не отсканировано')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map(it => {
                const diff = (Number(it.actual) || 0) - (Number(it.expected) || 0);
                const tone = diff < 0 ? 'var(--danger)' : diff > 0 ? 'var(--success)' : 'var(--fg-muted)';
                return (
                  <div key={it.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: 12, borderRadius: 'var(--r-md)',
                    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</p>
                      <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>
                        {t('inventory.expectedShort', 'по программе')} {it.expected} → {t('inventory.fact', 'факт')} {it.actual} {it.unit}
                      </p>
                    </div>
                    <span style={{ fontSize: 15, fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums' }}>
                      {diff > 0 ? `+${diff}` : diff}
                    </span>
                    <button onClick={() => removeItem(it.id)} style={{
                      border: 'none', background: 'transparent', color: 'var(--fg-faint)', cursor: 'pointer', padding: 4,
                    }}>
                      <Icon name="x" size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Действия */}
          <div style={{
            position: 'fixed', left: 0, right: 0, bottom: 0,
            padding: 16, background: 'var(--bg)', borderTop: '1px solid var(--border)',
            display: 'flex', gap: 10, maxWidth: 640, margin: '0 auto',
          }}>
            <button className="btn btn-ghost" onClick={cancel} style={{ flex: 1 }}>
              {t('common.cancel', 'Отмена')}
            </button>
            <button className="btn btn-primary" onClick={finish} disabled={items.length === 0 || closing} style={{ flex: 2 }}>
              {closing ? t('common.saving', 'Сохраняем…') : t('inventory.finish', 'Завершить')}
            </button>
          </div>
        </>
      ))}

      {/* ВКЛАДКА: История */}
      {tab === 'history' && (
        history === null ? (
          <p style={{ color: 'var(--fg-muted)' }}>{t('common.loading', 'Загрузка…')}</p>
        ) : history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--fg-faint)' }}>
            <Icon name="list" size={40} />
            <p style={{ marginTop: 12, fontSize: 14 }}>{t('inventory.noHistory', 'Завершённых инвентаризаций пока нет')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map(s => (
              <div key={s.id} style={{ borderRadius: 'var(--r-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', overflow: 'hidden' }}>
                <button onClick={() => openDetail(s.id)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: 12, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 700 }}>{fmtDate(s.closedAt)}</p>
                    <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>
                      {t('inventory.positions', 'Позиций')}: {s.count}
                      {s.shortage > 0 && <span style={{ color: 'var(--danger)' }}> · {t('inventory.shortage', 'Недостача')} −{s.shortage}</span>}
                      {s.surplus > 0 && <span style={{ color: 'var(--success)' }}> · {t('inventory.surplus', 'Излишек')} +{s.surplus}</span>}
                    </p>
                    {s.shortageCost > 0 && (
                      <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2 }}>
                        {t('inventory.lossCost', 'Потери')}: {formatMoney(s.shortageCost, currency)}
                      </p>
                    )}
                  </div>
                  <Icon name={detail && detail.id === s.id ? 'chevronDown' : 'chevronRight'} size={16} />
                </button>
                {detail && detail.id === s.id && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(detail.items || []).map(it => {
                      const diff = (Number(it.actual) || 0) - (Number(it.expected) || 0);
                      const tone = diff < 0 ? 'var(--danger)' : diff > 0 ? 'var(--success)' : 'var(--fg-muted)';
                      return (
                        <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
                          <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{it.expected} → {it.actual}</span>
                          <span style={{ fontSize: 13, fontWeight: 800, color: tone, minWidth: 36, textAlign: 'right' }}>{diff > 0 ? `+${diff}` : diff}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* Модалка ввода факта */}
      {scan && (
        <div className="modal-backdrop" onClick={() => setScan(null)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <p className="modal-title">{scan.name}</p>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 12 }}>
              {t('inventory.expected', 'По программе')}: <b>{scan.expected} {scan.unit}</b>
            </p>
            <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('inventory.factLabel', 'Фактический остаток')}</label>
            <input
              className="input"
              type="number"
              inputMode="decimal"
              autoFocus
              value={actual}
              onChange={e => setActual(e.target.value)}
              style={{ marginTop: 6, fontSize: 20, fontWeight: 700, textAlign: 'center' }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => setScan(null)} style={{ flex: 1 }}>
                {t('common.cancel', 'Отмена')}
              </button>
              <button className="btn btn-primary" onClick={saveActual} disabled={busy} style={{ flex: 1 }}>
                {busy ? t('common.saving', 'Сохраняем…') : t('common.save', 'Сохранить')}
              </button>
            </div>
          </div>
        </div>
      )}

      {ToastView}
    </div>
  );
}

function Chip({ label, value, tone }) {
  const color = tone === 'danger' ? 'var(--danger)' : tone === 'success' ? 'var(--success)' : 'var(--fg)';
  return (
    <div style={{
      padding: '8px 12px', borderRadius: 'var(--r-sm)',
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{label}: </span>
      <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}</span>
    </div>
  );
}
